<template>
  <div class="data-table-container">
    <div class="data-table-header">
      <h3 v-if="title" class="data-table-title">{{ title }}</h3>
      <DsButton
        v-if="data.length > 0"
        icon="file-spreadsheet"
        size="small"
        @click="downloadCSV"
      />
    </div>
    <table class="data-table">
      <thead>
        <tr>
          <th
            v-for="header in headers"
            :key="header.key"
            :style="{ width: header.width || 'auto' }"
            :class="{ 'text-right': header.align === 'right' }"
          >
            {{ header.label }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(row, index) in data"
          :key="index"
          @click="$emit('row-click', row)"
          class="data-table-row"
        >
          <td
            v-for="header in headers"
            :key="header.key"
            :class="{ 'text-right': header.align === 'right' }"
          >
            <slot
              :name="header.key"
              :row="row"
              :value="row[header.dataKey || header.key]"
            >
              {{ row[header.dataKey || header.key] }}
            </slot>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import DsButton from '../DsButton/DsButton.vue'
import Icon from '../Icon/Icon.vue'

interface Header {
  key: string
  label: string
  dataKey?: string
  width?: string
  align?: 'left' | 'right' | 'center'
}

export default defineComponent({
  name: 'DataTable',
  components: {
    DsButton,
    Icon
  },
  props: {
    headers: {
      type: Array as () => Header[],
      required: true
    },
    data: {
      type: Array as () => Record<string, any>[],
      required: true
    },
    downloadFileName: {
      type: String,
      default: 'table-data'
    },
    title: {
      type: String,
      default: ''
    }
  },
  emits: ['row-click'],
  methods: {
    downloadCSV() {
      if (!this.data.length) return;

      // Create headers row
      const headers = this.headers.map(header => header.label);

      // Create data rows
      const rows = this.data.map(row => {
        return this.headers.map(header => {
          const value = row[header.dataKey || header.key];
          // Handle null/undefined values and escape commas
          return value == null ? '' : String(value).replace(/,/g, ';');
        });
      });

      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const filename = `${this.downloadFileName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.csv`;
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }
  }
})
</script>

<style scoped lang="scss">
.data-table-container {
  width: 100%;
  overflow-x: auto;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.data-table-header {
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #eee;
}

.data-table-title {
  margin: 0;
  color: #333;
  font-size: 1.2rem;
  font-weight: 500;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;

  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid #eee;
  }

  th {
    font-weight: 600;
    color: #666;
    background: #f8f9fa;
  }

  tr:last-child td {
    border-bottom: none;
  }

  .text-right {
    text-align: right;
  }

  .data-table-row {
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: #f8f9fa;
    }
  }
}
</style>
